/** @jsx jsx */

import { ChangeEvent, useState } from 'react';

import {
  Button,
  Grid,
  IconButton,
  Text,
  TextInput,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import AutoCompleteTextInput from 'app/common/components/AutoCompleteTextInput';
import { quickCopy } from 'app/developer-v3/utils';

const Styles = {
  root: ({ isDisabled }) => [
    isDisabled &&
      css`
        user-select: none;
        opacity: 0.7;
        cursor: default;
      `,
  ],
};

export type KeyValue = {
  key: string;
  value: string;
  isKeyReadOnly?: boolean;
  isInvalidKey?: boolean;
  isDisabled?: boolean; // Applies to both key and value
};

type Props = {
  canChangeItemCount: boolean;
  enableAutoComplete: boolean;
  getKeyPlaceholder?: (item: KeyValue) => string;
  getValuePlaceholder?: (item: KeyValue) => string;
  initialItems: Array<KeyValue>;
  isDisabled: boolean;
  onUpdate: (options: Array<KeyValue>) => void;
  showHeaders?: boolean;
  uppercaseKeys?: boolean;
};

const getDefaultItem = (): KeyValue => ({ key: '', value: '' });
let deleteTimeout = null;

const defaultProps = {
  canChangeItemCount: true,
  enableAutoComplete: false,
  getKeyPlaceholder: () => 'key',
  getValuePlaceholder: () => 'value',
  initialItems: [],
  isDisabled: false,
};

function KeyValueEditor(props: Props) {
  const {
    canChangeItemCount = defaultProps.canChangeItemCount,
    enableAutoComplete = defaultProps.enableAutoComplete,
    getKeyPlaceholder = defaultProps.getKeyPlaceholder,
    getValuePlaceholder = defaultProps.getValuePlaceholder,
    initialItems = defaultProps.initialItems,
    isDisabled = defaultProps.isDisabled,
    onUpdate,
    showHeaders,
    uppercaseKeys,
  } = props;

  const [items, setItems] = useState<KeyValue[]>(
    initialItems.length > 0 ? quickCopy(initialItems) : [getDefaultItem()]
  );
  const [indexPendingDeletion, setIndexPendingDeletion] = useState(-1);

  const resetIndexPendingDeletion = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout);
      deleteTimeout = undefined;
    }
    setIndexPendingDeletion(-1);
  };

  const updateItems = (nextItems: KeyValue[]) => {
    const cleanItems = nextItems.filter(item => item.key || item.value);
    setItems(cleanItems);
    onUpdate(cleanItems);
  };

  const onClickAdd = () => {
    setItems([...items, getDefaultItem()]);
  };

  const onClickDelete = (index: number) => {
    if (indexPendingDeletion === index) {
      const nextItems = [...items];
      nextItems.splice(index, 1);
      updateItems(nextItems);
      resetIndexPendingDeletion();
    } else {
      setIndexPendingDeletion(index);

      deleteTimeout = setTimeout(() => {
        resetIndexPendingDeletion();
      }, 3000);
    }
  };

  const getPlaceholder = (type: 'key' | 'value', item: KeyValue) =>
    type === 'key' ? getKeyPlaceholder(item) : getValuePlaceholder(item);

  const onChangeInput = (index: number, name: 'key' | 'value') => (
    value: string
  ) => {
    const nextItems = [...items];
    nextItems[index][name] = value;
    updateItems(nextItems);
  };

  const getCommonInputProps = (
    type: 'key' | 'value',
    item: KeyValue,
    index: number,
    shouldDisable: boolean,
    shouldUppercaseKeys?: boolean
  ) => ({
    placeholder: getPlaceholder(type, item),
    name: type,
    value: shouldUppercaseKeys ? item[type].toUpperCase() : item[type],
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      onChangeInput(index, type)(e.target.value),
    onChangeValue: onChangeInput(index, type),
    isDisabled: item.isDisabled,
    isReadonly: shouldDisable || (type === 'key' && item.isKeyReadOnly),
    fauxReadOnly: shouldDisable || (type === 'key' && item.isKeyReadOnly),
  });

  return (
    <div css={Styles.root({ isDisabled })}>
      <Grid columns={1} gap={10}>
        {showHeaders && (
          <Grid
            columns={`repeat(2, 1fr) ${canChangeItemCount ? ' 45px' : ''}`}
            gap={10}
          >
            <Text fontWeight={600} tag="h3" type="Title">
              Key
            </Text>
            <Text fontWeight={600} tag="h3" type="Title">
              Value
            </Text>
          </Grid>
        )}
        {items.map((item, index) => (
          <Grid
            columns={`repeat(2, 1fr) ${canChangeItemCount ? ' 45px' : ''}`}
            gap={10}
            key={index}
          >
            <TextInput
              {...getCommonInputProps(
                'key',
                item,
                index,
                isDisabled,
                uppercaseKeys
              )}
              aria-label="Key"
              isErrored={item.isInvalidKey}
            />
            {enableAutoComplete && !isDisabled && !item?.isDisabled ? (
              <AutoCompleteTextInput
                {...getCommonInputProps('value', item, index, false)}
              />
            ) : (
              <TextInput
                {...getCommonInputProps('value', item, index, isDisabled)}
                aria-label="Value"
              />
            )}
            {canChangeItemCount && !item?.isDisabled && (
              <IconButton
                color={
                  indexPendingDeletion === index
                    ? 'icon-primary'
                    : 'icon-secondary'
                }
                icon={
                  indexPendingDeletion === index ? 'actionTrashFill' : 'formX'
                }
                label="Remove item"
                onClick={onClickDelete.bind(null, index)}
                size="medium"
              />
            )}
          </Grid>
        ))}
        {canChangeItemCount && (
          <Grid columns="repeat(2,max-content)">
            <Button
              color="secondary"
              disabled={isDisabled}
              iconBefore="formAdd"
              onClick={onClickAdd}
            >
              Add
            </Button>
          </Grid>
        )}
      </Grid>
    </div>
  );
}

KeyValueEditor.defaultProps = defaultProps;

export default KeyValueEditor;
